"use client";

import {
  FormControlLabel,
  FormHelperText,
  IconButton,
  Checkbox as MuiCheckBox,
} from "@mui/material";
import { useEffect } from "react";
import { FieldValues, useController } from "react-hook-form";
import { CheckBoxProps } from "../lib/type";
import { parseBoolean } from "@/src/shared/utils/dataTypeHelpers";
import { validationRules } from "@/src/shared/utils/validationRules";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import { WebShopIcon } from "@/src/components/WebShopIcon";

export const parseLinksAsDOMNode = (text: string) => {
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

  const parts = text.split(linkRegex);

  return parts.map((part, index) => {
    if (index % 3 === 0) return part;

    if (index % 3 === 1) {
      const linkText = part;
      const url = parts[index + 1];

      return (
        <a
          key={index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#009DDE",
            fontSize: "15px",
            lineHeight: "22px",
            textDecoration: "underline",
          }}
        >
          {linkText}
        </a>
      );
    }

    return null;
  });
};

export const CheckBox = <T extends FieldValues>({
  title,
  control,
  name,
  label,
  hintIcon,
  toggleHintArea,
  openHintArea,
  closeHintArea,
  formControlLabelProps,
  disabled,
  required,
  deps,
  onChange: onChangeController,
  withMobileHint = false,
  ...rest
}: CheckBoxProps<T>) => {
  const {
    field: { onChange, value, ref, name: fieldName },
    fieldState: { error },
  } = useController({
    control,
    name,
    rules: validationRules.Checkbox({ required, disabled, deps }),
  });
  const isChecked = parseBoolean(value);
  useEffect(() => {
    if (openHintArea && closeHintArea && !withMobileHint) {
      isChecked ? openHintArea() : closeHintArea();
    }
  }, [value]);

  const parsedLabel = parseLinksAsDOMNode(label || "");

  return (
    <>
      <div className="flex gap-4">
        <FormControlLabel
          {...formControlLabelProps}
          control={
            <MuiCheckBox
              {...rest}
              disabled={disabled}
              onChange={(e, value) => {
                onChange({ target: { value } });
                if (onChangeController) {
                  onChangeController(e, value);
                }
              }}
              value={value}
              ref={ref}
              name={fieldName}
              checked={isChecked}
              // sx={{ paddingLeft: 0 }}
            />
          }
          label={parsedLabel}
          sx={{ margin: 0 }}
        />
        {hintIcon && withMobileHint && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              toggleHintArea?.();
            }}
            className={cn("px-2", {
              hidden: !hintIcon,
              flex: hintIcon,
              "md:hidden": true,
            })}
          >
            <WebShopIcon name={"questionCircle"} />
          </IconButton>
        )}
      </div>
      {error && <FormHelperText error>{error.message}</FormHelperText>}
    </>
  );
};
